import React, { useState, useEffect, useRef, useCallback } from 'react';
import { host } from '../script/variables';
import '../style/AdminRestaurantReview.css';

/* ─── tiny helpers ─────────────────────────────────────────────────────────── */
const API = `${host}/admin/restaurants`;

const fmt     = (n) => (n ?? 0).toLocaleString();
const timeStr = (h) => (h == null ? '—' : `${String(h).padStart(2, '0')}:00`);

/* ─── fetch helper (credentials included on every call) ───────────────────── */
async function apiFetch(url, options = {}) {
  const res = await fetch(url, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.message || `Request failed: ${res.status}`);
  }
  return res.json();
}

/* ─── Sub-components ───────────────────────────────────────────────────────── */

function Badge({ label, color }) {
  const colors = {
    amber:   'var(--ad-amber)',
    emerald: 'var(--ad-emerald)',
    red:     'var(--ad-red)',
    cyan:    'var(--ad-cyan)',
    violet:  'var(--ad-violet)',
    muted:   'var(--ad-text-muted)',
  };
  const c = colors[color] || colors.muted;
  return (
    <span
      className="rr-badge"
      style={{
        color: c,
        background: `${c}18`,
        border: `1px solid ${c}30`,
      }}
    >
      {label}
    </span>
  );
}

function MetaRow({ icon, label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="rr-meta-row">
      <span className="rr-meta-icon">{icon}</span>
      <span className="rr-meta-label">{label}</span>
      <span className="rr-meta-value">{value}</span>
    </div>
  );
}

function Spinner() {
  return (
    <div className="rr-spinner-wrap">
      <div className="rr-spinner" />
    </div>
  );
}

/* ─── Menu Modal ───────────────────────────────────────────────────────────── */
function MenuModal({ cafeId, cafeName, onClose }) {
  const [menu, setMenu]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState('');

  useEffect(() => {
    apiFetch(`${API}/${cafeId}/menu`)
      .then(data => setMenu(data.data.menu))
      .catch(e => setErr(e.message || 'Failed to load menu'))
      .finally(() => setLoading(false));
  }, [cafeId]);

  return (
    <div className="rr-modal-overlay" onClick={onClose}>
      <div className="rr-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="rr-modal-header">
          <div>
            <p className="rr-modal-label">MENU REVIEW</p>
            <h2 className="rr-modal-title">{cafeName}</h2>
          </div>
          <button className="rr-modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Body */}
        <div className="rr-modal-body">
          {loading && <Spinner />}
          {err && <p className="rr-modal-error">{err}</p>}
          {menu && menu.length === 0 && (
            <p className="rr-modal-empty">No menu items found.</p>
          )}
          {menu && menu.map((section, si) => (
            <div key={si} className="rr-menu-section">
              <div className="rr-menu-section-header">
                <span className="rr-menu-type-badge">{section.type}</span>
                <div className="rr-menu-section-divider" />
              </div>

              {section.categories.map((cat, ci) => (
                <div key={ci} className="rr-menu-category">
                  <p className="rr-menu-cat-name">{cat.name}</p>
                  <div className="rr-dish-grid">
                    {cat.dishes.map((dish, di) => (
                      <div key={di} className="rr-dish-card">
                        {dish.image
                          ? <img src={dish.image} alt={dish.name} className="rr-dish-img"
                              onError={e => { e.target.style.display = 'none'; }} />
                          : <div className="rr-dish-placeholder">🍽️</div>
                        }
                        <div className="rr-dish-body">
                          <p className="rr-dish-name">{dish.name}</p>
                          <div className="rr-dish-prices">
                            {['small', 'medium', 'large'].map(size =>
                              dish[size]?.price != null && (
                                <span key={size} className="rr-dish-price">
                                  {size[0].toUpperCase()}: ₹{dish[size].price}
                                  {dish[size].discount > 0 && (
                                    <span className="rr-dish-discount"> -{dish[size].discount}%</span>
                                  )}
                                </span>
                              )
                            )}
                          </div>
                          <div className="rr-dish-tags">
                            <Badge
                              label={dish.type}
                              color={dish.type === 'veg' ? 'emerald' : dish.type === 'nonveg' ? 'red' : 'amber'}
                            />
                            {!dish.isAvailable && <Badge label="UNAVAILABLE" color="muted" />}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Restaurant Card ──────────────────────────────────────────────────────── */
function RestaurantCard({ r, onVerify, onViewMenu }) {
  const [expanded, setExpanded]   = useState(false);
  const [verifying, setVerifying] = useState(false);

  const owner = r.owner || r.ownerid;

  const handleVerify = async (v) => {
    setVerifying(true);
    await onVerify(r._id, v);
    setVerifying(false);
  };

  return (
    <div className={`rr-card${r.isVerified ? ' rr-card--verified' : ''}`}>

      {/* Card header */}
      <div className="rr-card-header">

        {/* Thumbnail */}
        <div className="rr-thumbnail">
          {r.thumbnail
            ? <img src={r.thumbnail} alt=""
                onError={e => { e.target.style.display = 'none'; }} />
            : '🍴'}
        </div>

        {/* Name + badges */}
        <div className="rr-card-info">
          <div className="rr-card-title-row">
            <h3 className="rr-card-name">{r.name}</h3>
            {r.isVerified
              ? <Badge label="✓ Verified"  color="emerald" />
              : <Badge label="Unverified"  color="amber" />}
          </div>
          {r.title && <p className="rr-card-title-sub">{r.title}</p>}
          <div className="rr-card-badges">
          {  (Array.isArray(r.category) ? r.category : [r.category].filter(Boolean))
  .map((c, i) => <Badge key={i} label={c} color="cyan" />)}
            <Badge
              label={r.menuType || 'veg'}
              color={r.menuType === 'nonveg' ? 'red' : r.menuType === 'omnivores' ? 'violet' : 'emerald'}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="rr-card-actions">
          <button className="rr-btn-details" onClick={() => setExpanded(x => !x)}>
            {expanded ? '▲ Collapse' : '▼ Details'}
          </button>
          <button className="rr-btn-menu" onClick={() => onViewMenu(r._id, r.name)}>
            🍽 Menu
          </button>
        </div>
      </div>

      {/* Expanded metadata */}
      {expanded && (
        <div className="rr-card-detail">
          <div className="rr-detail-grid">
            <div>
              <p className="rr-detail-section-label">Restaurant Info</p>
              <MetaRow icon="🆔" label="Restaurant ID" value={r._id} />
              <MetaRow icon="📝" label="Description"   value={r.description} />
              <MetaRow icon="🌐" label="Website"       value={r.website !== '#' ? r.website : null} />
              <MetaRow icon="⏰" label="Opens"         value={timeStr(r.opensAt)} />
              <MetaRow icon="🔒" label="Closes"        value={timeStr(r.CloseAt)} />
              <MetaRow icon="📍" label="Coordinates"   value={r.location?.coordinates?.join(', ')} />
              <MetaRow icon="👁️" label="Views"         value={fmt(r.views)} />
              <MetaRow icon="❤️" label="Likes"         value={fmt(r.likes)} />
              <MetaRow icon="🔗" label="Shares"        value={fmt(r.shares)} />
              <MetaRow icon="💬" label="Comments"      value={fmt(r.totalComments)} />
            </div>
            <div>
              <p className="rr-detail-section-label">Owner Info</p>
              <MetaRow icon="👤" label="Owner ID"     value={owner?._id} />
              <MetaRow icon="📛" label="Name"         value={owner ? `${owner.firstName || ''} ${owner.lastName || ''}`.trim() : null} />
              <MetaRow icon="📧" label="Email"        value={owner?.email} />
              <MetaRow icon="📱" label="Phone"        value={owner?.phone} />
              <MetaRow icon="🔑" label="Role"         value={owner?.role} />
              <MetaRow icon="👁️" label="Account Type" value={owner?.accounttype} />
              <p className="rr-detail-section-label rr-detail-section-label--spaced">Tags</p>
              <MetaRow icon="🍜" label="Cuisine"  value={(r.cousine  || []).filter(Boolean).join(', ') || null} />
              <MetaRow icon="🎭" label="Ambience" value={(r.ambience || []).filter(Boolean).join(', ') || null} />
              <MetaRow icon="✨" label="Vibe"     value={(r.vibe     || []).filter(Boolean).join(', ') || null} />
              <MetaRow icon="🎉" label="Occasion" value={(r.occasion || []).filter(Boolean).join(', ') || null} />
            </div>
          </div>

          {/* Image strip */}
          {r.images?.length > 0 && (
            <div className="rr-image-strip">
              <p className="rr-image-strip-label">Images ({r.images.length})</p>
              <div className="rr-image-strip-scroll">
                {r.images.map((img, i) => (
                  <img key={i} src={img} alt="" className="rr-strip-img"
                    onError={e => { e.target.style.display = 'none'; }} />
                ))}
              </div>
            </div>
          )}

          {/* Verify action */}
          <div className="rr-verify-row">
            {r.isVerified ? (
              <button
                disabled={verifying}
                onClick={() => handleVerify(false)}
                className="rr-btn-revoke"
              >
                {verifying ? 'Updating…' : '✕ Revoke Verification'}
              </button>
            ) : (
              <button
                disabled={verifying}
                onClick={() => handleVerify(true)}
                className="rr-btn-verify"
              >
                {verifying ? 'Verifying…' : '✓ Verify Restaurant'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Section header ───────────────────────────────────────────────────────── */
function SectionHeader({ label, count, color }) {
  return (
    <div className="rr-section-header">
      <div className="rr-section-line-left" style={{ background: color }} />
      <span className="rr-section-label" style={{ color }}>{label}</span>
      <span className="rr-section-count">{count}</span>
      <div className="rr-section-line-right" />
    </div>
  );
}

/* ─── Main Component ───────────────────────────────────────────────────────── */
const RestaurantReview = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [page, setPage]               = useState(1);
  const [hasMore, setHasMore]         = useState(true);
  const [loading, setLoading]         = useState(false);
  const [firstLoad, setFirstLoad]     = useState(true);
  const [error, setError]             = useState('');
  const [search, setSearch]           = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filter, setFilter]           = useState('all'); // all | verified | unverified
  const [menuModal, setMenuModal]     = useState(null);  // { id, name }
  const [documentModel,setDocumentModel] = useState(null)
  const loaderRef   = useRef(null);
  const searchTimer = useRef(null);

  /* ── fetch page ─────────────────────────────────────────────────────────── */
  const fetchPage = useCallback(async (pageNum, reset = false) => {
    if (loading) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        page:  pageNum,
        limit: 12,
        filter,
        ...(search ? { search } : {}),
      });
      const data = await apiFetch(`${API}?${params}`);
      const incoming = data.data.restaurants;
      setRestaurants(prev => reset ? incoming : [...prev, ...incoming]);
      setHasMore(data.data.pagination.hasMore);
      setPage(pageNum);
    } catch (e) {
      setError(e.message || 'Failed to load restaurants');
    } finally {
      setLoading(false);
      setFirstLoad(false);
    }
  }, [search, filter, loading]);

  /* ── reset + fetch on filter / search change ────────────────────────────── */
  useEffect(() => {
    setRestaurants([]);
    setPage(1);
    setHasMore(true);
    setFirstLoad(true);
    fetchPage(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filter]);

  /* ── Infinite scroll observer ───────────────────────────────────────────── */
  useEffect(() => {
    const el = loaderRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !loading) fetchPage(page + 1);
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loading, page, fetchPage]);

  /* ── Verify handler ─────────────────────────────────────────────────────── */
  const handleVerify = async (id, verified) => {
    try {
      await apiFetch(`${API}/${id}/verify`, {
        method: 'PATCH',
        body:   JSON.stringify({ verified }),
      });
      setRestaurants(prev =>
        prev.map(r => r._id === id ? { ...r, isVerified: verified } : r)
      );
    } catch {
      alert('Failed to update verification status');
    }
  };

  /* ── Search debounce ────────────────────────────────────────────────────── */
  const handleSearchInput = (e) => {
    setSearchInput(e.target.value);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setSearch(e.target.value.trim()), 420);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearch('');
  };

  /* ── Split for sections ─────────────────────────────────────────────────── */
  const verified   = restaurants.filter(r =>  r.isVerified);
  const unverified = restaurants.filter(r => !r.isVerified);

  const renderList = (list) =>
    list.map(r => (
      <RestaurantCard
        key={r._id}
        r={r}
        onVerify={handleVerify}
        onViewMenu={(id, name) => setMenuModal({ id, name })}
      />
    ));

  /* ── UI ─────────────────────────────────────────────────────────────────── */
  return (
    <>
      <div className="rr-page">
        <div className="rr-container">

          {/* ── Page header ─────────────────────────────────────────────── */}
          {/* <div className="rr-page-header">
            <div className="rr-page-title-row">
              <div className="rr-title-bar" />
              <h1 className="rr-page-title">Restaurant Review</h1>
              <span className="rr-admin-badge">Admin</span>
            </div>
            <p className="rr-page-subtitle">
              Review metadata, verify restaurants and inspect menus before approval.
            </p>
          </div> */}

          {/* ── Search + filter bar ─────────────────────────────────────── */}
          <div className="rr-toolbar">
            <div className="rr-search-wrap">
              <span className="rr-search-icon">🔍</span>
              <input
                className="rr-search"
                value={searchInput}
                onChange={handleSearchInput}
                placeholder="Search by name, restaurant ID, owner ID, or phone…"
              />
              {searchInput && (
                <button className="rr-search-clear" onClick={clearSearch}>✕</button>
              )}
            </div>

            {['all', 'unverified', 'verified'].map(f => (
              <button
                key={f}
                className={`rr-filter-btn${filter === f ? ' rr-filter-btn--active' : ''}`}
                onClick={() => setFilter(f)}
              >
                {f === 'all' ? '⊘ All' : f === 'verified' ? '✓ Verified' : '⚠ Unverified'}
              </button>
            ))}
          </div>

          {/* ── Error ───────────────────────────────────────────────────── */}
          {error && <div className="rr-error">{error}</div>}

          {/* ── First load skeleton ─────────────────────────────────────── */}
          {firstLoad && loading && <Spinner />}

          {/* ── Restaurant lists ─────────────────────────────────────────── */}
          {!firstLoad && (
            <>
              {(filter === 'all' || filter === 'unverified') && (
                <>
                  <SectionHeader label="Pending Verification" count={unverified.length} color="var(--ad-amber)" />
                  {unverified.length === 0
                    ? <p className="rr-empty">No unverified restaurants.</p>
                    : <div className="rr-card-list">{renderList(unverified)}</div>
                  }
                </>
              )}

              {(filter === 'all' || filter === 'verified') && (
                <>
                  <SectionHeader label="Verified" count={verified.length} color="var(--ad-emerald)" />
                  {verified.length === 0
                    ? <p className="rr-empty">No verified restaurants.</p>
                    : <div className="rr-card-list">{renderList(verified)}</div>
                  }
                </>
              )}
            </>
          )}

          {/* ── Infinite scroll loader sentinel ─────────────────────────── */}
          <div ref={loaderRef} style={{ height: 1 }} />
          {loading && !firstLoad && <Spinner />}

          {!hasMore && !loading && restaurants.length > 0 && (
            <p className="rr-end-label">── end of results ──</p>
          )}
        </div>
      </div>

      {/* ── Menu Modal ────────────────────────────────────────────────────── */}
      {menuModal && (
        <MenuModal
          cafeId={menuModal.id}
          cafeName={menuModal.name}
          onClose={() => setMenuModal(null)}
        />
      )}


      
    </>
  );
};

export default RestaurantReview;